<script setup>
import { produktnavn } from '@/Composables/produktnavn';
import BrandDisplay from '@/Components/Product/BrandDisplay.vue';
import { computed } from 'vue';

const props = defineProps({
    product: {
        type: Object,
        required: true,
    },
    activeFilters: {
        type: Object,
        default: () => ({}),
    },
});

const thumbnailUrl = computed(() => {
    if (props.product.images && props.product.images.length > 0) {
        return `/images/${props.product.images[0].id}`;
    }
    return null;
});

const formattedPrice = computed(() => {
    const value = Number(props.product.prices?.lowest_price_including_shipping);
    if (isNaN(value)) return '';
    return new Intl.NumberFormat('da-DK', {
        style: 'currency',
        currency: 'DKK',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value);
});

const retailerCount = computed(() => props.product.prices?.total_retailers ?? 0);

const stockStatus = computed(() => {
    const lowestHasStock = props.product.prices?.lowest_price_has_in_stock;
    const othersHaveStock = props.product.prices?.others_has_in_stock;
    if (lowestHasStock || othersHaveStock) {
        return { inStock: true, label: 'På lager', icon: '✓' };
    }
    return { inStock: false, label: 'Ikke på lager', icon: '✗' };
});

const extraAttributes = computed(() => {
    const base = Array.isArray(props.product.attribute_texts) ? [...props.product.attribute_texts] : [];
    if (!props.product.attributes || !props.activeFilters) return base.map(text => ({ text, isExtra: false }));
    const filterIds = Object.keys(props.activeFilters).map(Number);
    const alreadyIncluded = new Set(base);
    const extras = [];
    for (const attr of props.product.attributes) {
        if (
            filterIds.includes(attr.filter_id) &&
            typeof attr.value === 'string' &&
            !alreadyIncluded.has(attr.value)
        ) {
            extras.push({ text: attr.value, isExtra: true });
            if (extras.length >= 3) break;
        }
    }
    return base.map(text => ({ text, isExtra: false })).concat(extras);
});
</script>

<template>
    <a :href="product.route_to_productdetail" class="product-compact-row">
        <div class="col-thumbnail">
            <div class="thumbnail-container">
                <picture v-if="thumbnailUrl">
                    <source :srcset="`${thumbnailUrl}?w=96&fmt=webp`" type="image/webp" />
                    <img
                        :src="`${thumbnailUrl}?w=96`"
                        :alt="produktnavn(product)"
                        class="thumbnail-img"
                        loading="lazy"
                        decoding="async"
                        width="32"
                        height="32"
                    />
                </picture>
                <div v-else class="thumbnail-placeholder"></div>
            </div>
        </div>

        <div class="col-brand">
            <BrandDisplay :brand="product.brand" />
        </div>

        <div class="col-name">
            <span class="product-name">{{ produktnavn(product) }}</span>
        </div>

        <div class="col-specs">
            <span v-for="(attr, idx) in extraAttributes" :key="`${attr.text}-${idx}`">
                <span :class="{ 'extra-attribute': attr.isExtra }">{{ attr.text }}</span>
                <span v-if="idx !== extraAttributes.length - 1" class="divider">|</span>
            </span>
        </div>

        <div class="col-price-stock">
            <span class="price">{{ formattedPrice }}</span>
            <span class="retailer-badge">🛒 {{ retailerCount }}</span>
            <span :class="['stock-indicator', stockStatus.inStock ? 'in-stock' : 'out-of-stock']">
                {{ stockStatus.icon }}
            </span>
        </div>
    </a>
</template>

<style scoped>
.product-compact-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 16px;
    height: 40px;
    border-bottom: 1px solid var(--pc-border, #2a2a4a);
    text-decoration: none;
    color: var(--pc-text-primary, #e0e0ff);
    font-size: var(--pc-text-sm, 13px);
    transition: background var(--pc-transition, 150ms) ease;
}

.product-compact-row:hover {
    background: var(--pc-bg-hover, rgba(255, 255, 255, 0.03));
}

.col-thumbnail {
    flex: 0 0 40px;
}

.thumbnail-container {
    width: 32px;
    height: 32px;
    border-radius: 6px;
    overflow: hidden;
    background: #f0f0f0;
    display: flex;
    align-items: center;
    justify-content: center;
}

.thumbnail-img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}

.thumbnail-placeholder {
    width: 100%;
    height: 100%;
    background: #f0f0f0;
}

.col-brand {
    flex: 0 0 64px;
    overflow: hidden;
    font-size: 11px;
}

.col-name {
    flex: 1 1 0;
    min-width: 0;
}

.product-name {
    font-size: var(--pc-text-sm, 13px);
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;
}

.col-specs {
    flex: 1 1 0;
    min-width: 0;
    font-size: 11px;
    color: var(--pc-text-secondary, #8888aa);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.divider {
    padding: 0 3px;
    color: var(--pc-text-muted, #555577);
}

.extra-attribute {
    font-weight: 600;
    color: var(--pc-accent, #6366f1);
}

.col-price-stock {
    flex: 0 0 140px;
    display: flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
}

.price {
    color: var(--pc-price, #60a5fa);
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    font-size: var(--pc-text-sm, 13px);
}

.retailer-badge {
    font-size: 11px;
    color: var(--pc-text-secondary, #8888aa);
}

.stock-indicator {
    font-size: 12px;
    font-weight: 600;
}

.stock-indicator.in-stock {
    color: var(--pc-success, #22c55e);
}

.stock-indicator.out-of-stock {
    color: var(--pc-danger, #ef4444);
}
</style>
